import { stopPostHogSessionRecording } from '@client/utils/feature-flags/posthogClient';
import * as Sentry from '@sentry/react';
import type { AnyRouter } from '@tanstack/react-router';
import { preventRecordingForLocation } from './recording-privacy';

type ReplayIntegration = ReturnType<typeof Sentry.replayIntegration>;

/**
 * `Sentry.getReplay()` without its import: `@sentry/replay` is one 214 KB module, so naming
 * `getReplay` from this boot-time file put all of Replay in the boot graph even though the
 * integration itself is only added on idle (`config/sentry-deferred.ts`). The SDK's own
 * `getReplay` is exactly this lookup.
 */
function getActiveReplay(): ReplayIntegration | undefined {
    return Sentry.getClient()?.getIntegrationByName<ReplayIntegration>('Replay');
}

/** Stop as navigation begins without adding a history blocker. The resolved listener covers
 * history traversal and redirects. The Replay transport independently drops
 * secret-bearing recordings, including the traversal-to-listener window.
 */
export function trackSecretLocationRecordingGate(router: AnyRouter): () => void {
    const stopForLocation = (pathname: string, search: string) => {
        if (preventRecordingForLocation(pathname, search)) {
            // Recorder failures must never abort navigation or prevent the other
            // recorder from stopping. The transport guard also fails closed.
            try {
                void getActiveReplay()
                    ?.stop({ flush: false })
                    .catch(() => {});
            } catch {
                // The SDK may throw before returning a promise.
            }
            try {
                stopPostHogSessionRecording();
            } catch {
                // Telemetry must not interfere with routing.
            }
        }
    };
    const unsubscribeBefore = router.subscribe('onBeforeNavigate', ({ toLocation }) => {
        stopForLocation(toLocation.pathname, toLocation.searchStr);
    });
    const unsubscribe = router.subscribe('onResolved', ({ toLocation }) => {
        stopForLocation(toLocation.pathname, toLocation.searchStr);
    });
    return () => {
        unsubscribeBefore();
        unsubscribe();
    };
}
